import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject, type OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { Group } from '../../../shared/models/api/group.model';
import { GroupService } from '../../../shared/services/api/group.service';

@Component({
  selector: 'opa-group-delete',
  imports: [CommonModule, PorscheDesignSystemModule],
  templateUrl: './group-delete.component.html',
  styleUrl: './group-delete.component.scss',
})
export class GroupDeleteComponent implements OnDestroy {
  private readonly groupService = inject(GroupService);
  private readonly toastManager = inject(ToastManager);
  private readonly router = inject(Router);

  @Input({ required: true }) open = false;
  @Input({ required: true }) group: Group | undefined = undefined;
  @Output() dismiss = new EventEmitter<Group | undefined>();

  private readonly subSink = new SubSink();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(group?: Group): void {
    this.dismiss.emit(group);
  }

  submit(): void {
    const group = this.group;
    if (!group) return;

    this.submitting.next(true);
    this.subSink.sink = this.groupService
      .deleteGroup(group.id)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: `Group ${group.name} deleted`,
              state: 'success',
            });
            this.submitting.next(false);
            this.router.navigate(['/groups']);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }
}
